import type BitSet from '../BitSet.ts'
import type Chunk from '../Chunk.ts'
import type { SourceMapOptions, SourceMapSegment } from '../SourceMap.ts'
import type { SourceLocation } from './getLocator.ts'

const wordRegex = /\w/

export default class Mappings {
  declare hires: SourceMapOptions['hires']
  declare generatedCodeLine: number
  declare generatedCodeColumn: number
  declare raw: SourceMapSegment[][]
  declare rawSegments: SourceMapSegment[]
  declare pending: SourceMapSegment | null

  constructor(hires: SourceMapOptions['hires']) {
    this.hires = hires
    this.generatedCodeLine = 0
    this.generatedCodeColumn = 0
    this.raw = []
    this.rawSegments = this.raw[this.generatedCodeLine] = []
    this.pending = null
  }

  addEdit(sourceIndex: number, content: string, loc: SourceLocation, nameIndex: number): void {
    if (content.length) {
      const contentLengthMinusOne = content.length - 1
      let contentLineEnd = content.indexOf('\n', 0)
      let previousContentLineEnd = -1
      // Loop through each line in the content and add a segment, but stop if the last line is empty,
      // else code afterwards would fill one line too many
      while (contentLineEnd >= 0 && contentLengthMinusOne > contentLineEnd) {
        const segment: SourceMapSegment = [
          this.generatedCodeColumn,
          sourceIndex,
          loc.line,
          loc.column,
        ]
        if (nameIndex >= 0) {
          segment.push(nameIndex)
        }
        this.rawSegments.push(segment)

        this.generatedCodeLine += 1
        this.raw[this.generatedCodeLine] = this.rawSegments = []
        this.generatedCodeColumn = 0

        previousContentLineEnd = contentLineEnd
        contentLineEnd = content.indexOf('\n', contentLineEnd + 1)
      }

      const segment: SourceMapSegment = [
        this.generatedCodeColumn,
        sourceIndex,
        loc.line,
        loc.column,
      ]
      if (nameIndex >= 0) {
        segment.push(nameIndex)
      }
      this.rawSegments.push(segment)

      this.advance(content.slice(previousContentLineEnd + 1))
    }
    else if (this.pending) {
      this.rawSegments.push(this.pending)
      this.advance(content)
    }

    this.pending = null
  }

  addUneditedChunk(
    sourceIndex: number,
    chunk: Chunk,
    original: string,
    loc: SourceLocation,
    sourcemapLocations: BitSet,
  ): void {
    let originalCharIndex = chunk.start
    let first = true
    // when iterating each char, check if it's in a word boundary
    let charInHiresBoundary = false

    while (originalCharIndex < chunk.end) {
      if (original[originalCharIndex] === '\n') {
        loc.line += 1
        loc.column = 0
        this.generatedCodeLine += 1
        this.raw[this.generatedCodeLine] = this.rawSegments = []
        this.generatedCodeColumn = 0
        first = true
        charInHiresBoundary = false
      }
      else {
        if (this.hires || first || sourcemapLocations.has(originalCharIndex)) {
          const segment: SourceMapSegment = [
            this.generatedCodeColumn,
            sourceIndex,
            loc.line,
            loc.column,
          ]

          if (this.hires === 'boundary') {
            // in hires "boundary", group segments per word boundary than per char
            if (wordRegex.test(original[originalCharIndex])) {
              // for first char in the boundary found, start the boundary by pushing a segment
              if (!charInHiresBoundary) {
                this.rawSegments.push(segment)
                charInHiresBoundary = true
              }
            }
            else {
              // for non-word char, end the boundary by pushing a segment
              this.rawSegments.push(segment)
              charInHiresBoundary = false
            }
          }
          else {
            this.rawSegments.push(segment)
          }
        }

        loc.column += 1
        this.generatedCodeColumn += 1
        first = false
      }

      originalCharIndex += 1
    }

    this.pending = null
  }

  advance(str: string): void {
    if (!str)
      return

    const lines = str.split('\n')

    if (lines.length > 1) {
      for (let i = 0; i < lines.length - 1; i++) {
        this.generatedCodeLine++
        this.raw[this.generatedCodeLine] = this.rawSegments = []
      }
      this.generatedCodeColumn = 0
    }

    this.generatedCodeColumn += lines[lines.length - 1].length
  }
}
